import environment from '../../../common/environment';
import store from '../../../store/store';
import { FreeTrialInfo, FreeTrialStatus } from '../interfaces/free-trial-info.interface';
import { MagentoOrderRequest, PaymentTypeMagento } from '../../checkout/shop-checkout.interface';
import { Product } from '../../cart/shop-cart/interfaces/shop-cart.interface';
import { REDIRECT_URL_HECLR_KEY } from '../../../common/constants/api-constants';
import { getDateFromTimestamp, SECONDS_PER_DAY } from '../../../common/utils/date-utils';
import { StoreState } from '../../../store/store.interface';
import { getCart } from '../../cart/shop-cart/store/shop-cart.selectors';

export const CANCELATION_FREE_TRIAL_HELP_URL = `${environment().cancelFreeTrialHelpUrl}`;

export const isInactiveFreeTrial = (freeTrialInfo: FreeTrialInfo): boolean => {
  return (
    !!freeTrialInfo.isFreeTrial &&
    (freeTrialInfo.status == FreeTrialStatus.CANCELED || freeTrialInfo.status == FreeTrialStatus.EXPIRED)
  );
};

export const isActiveFreeTrial = (freeTrialInfo: FreeTrialInfo): boolean => {
  return !!freeTrialInfo.isFreeTrial && freeTrialInfo.status == FreeTrialStatus.ACTIVE;
};

/**
 * Returns the first free trial product from the cart.
 */
export function getFreeTrialProduct(): Product | undefined {
  const products = getCart(store.getState() as StoreState)?.products ?? [];
  return products.find((product) => product?.freeTrialInfo && product.freeTrialInfo?.isFreeTrial);
}

/**
 * Returns all free trial products from the cart.
 */
export function getAllFreeTrialProducts(): Product[] {
  const products = getCart(store.getState() as StoreState)?.products ?? [];
  return products.filter((product) => product?.freeTrialInfo && product.freeTrialInfo?.isFreeTrial);
}

/**
 * Calculates the duration of the free trial in days
 *
 * @param freeTrialStartDate the start date of the free trial
 * @param freeTrialEndDate the end date of the free trial
 */
export function getFreeTrialDuration(freeTrialStartDate?: string, freeTrialEndDate?: string): string {
  if (!freeTrialStartDate || !freeTrialEndDate) {
    return '0';
  }

  const durationInSeconds = parseInt(freeTrialEndDate!) - parseInt(freeTrialStartDate!);
  return (durationInSeconds / SECONDS_PER_DAY).toString();
}

export function getOrderHistoryPageURL(state: StoreState) {
  const orderHistoryURL = `${environment().mheducationUrl}/account#orderHistory`;
  if (state.userProfileState.userProfileResponse?.data) {
    return orderHistoryURL;
  }
  const url = new URL(environment().baseLoginUrl);
  url.searchParams.set(REDIRECT_URL_HECLR_KEY, orderHistoryURL);
  return url.toString();
}

/**
 * Updates the payment method to free trial
 *
 * @param magentoOrderRequest
 */
export function updateToFreeTrialPaymentMethod(magentoOrderRequest: MagentoOrderRequest): any {
  const paymentMethod = magentoOrderRequest.order.paymentMethod;
  paymentMethod.additional_data!.payment_method = paymentMethod.method;
  paymentMethod.method = PaymentTypeMagento.FREE_TRIAL;
  return paymentMethod;
}

/**
 * Replaces timestamps with full dates in the free trial info object.
 */
export function freeTrialTimestampsToDates(freeTrialInfo?: FreeTrialInfo): FreeTrialInfo|undefined {
  if (!freeTrialInfo) {
    return freeTrialInfo;
  }

  const startDate = freeTrialInfo.startDate;
  const enDate = freeTrialInfo.endDate;

  return {
    ...freeTrialInfo,
    startDate: startDate ? getDateFromTimestamp(startDate, true) : startDate,
    endDate: enDate ? getDateFromTimestamp(enDate, true): enDate,
  };
}
